import { type ReactNode, useRef } from 'react';
import { SerializedStyles } from '@emotion/react';
import { callIntelResponsiveStyles } from '../../views/common-styles';
import { ScrollClassTrigger } from '../scroll-class-trigger';

export interface FiltersSectionProps {
  /** The filter bar content */
  children: ReactNode;
  /** Additional content to render alongside filters (e.g., content switcher) */
  extra?: ReactNode;
  /** Custom wrapper styles */
  wrapperCss?: SerializedStyles;
  /** Ref to the filters wrapper element */
  filtersWrapperRef?: React.RefObject<HTMLDivElement>;
  /** Optional test id for the wrapper element */
  'data-testid'?: string;
}

/**
 * FiltersSection
 *
 * A reusable component for the filters section in call intel pages.
 * Provides consistent responsive behavior with Sticky positioning with scroll-based shadow effects
 */
export const FiltersSection = ({
  children,
  extra,
  wrapperCss,
  filtersWrapperRef: externalRef,
  'data-testid': testId,
}: FiltersSectionProps) => {
  const internalRef = useRef<HTMLDivElement | null>(null);
  const filtersWrapperRef = externalRef || internalRef;

  return (
    <>
      {/* This is used to show a bottom shadow on the filters bar when user scrolls past the filter section */}
      <ScrollClassTrigger targetRef={filtersWrapperRef} outOfViewClassName='sticky-bottom-shadow' />
      <div css={[callIntelResponsiveStyles.filtersWrapper, wrapperCss]} ref={filtersWrapperRef} data-testid={testId}>
        {children}
        {extra}
      </div>
    </>
  );
};
